// Menu de navigation global (bouton ☰), présent dans l'en-tête de chaque écran.
// Se construit à partir du registre SECTIONS_MENU : ajouter une section là-bas la
// fait apparaître ici automatiquement. La section courante est mise en évidence.
// « Se déconnecter » est en pied de menu (action, pas une section).

import { useEffect, useRef, useState } from 'react'
import { moduleOuvert } from '@shared/constants'
import { accesActif } from '@shared/calculs/abonnement'
import { SECTIONS_MENU, useNav, type SectionId } from '@/lib/nav'
import { useAuth } from '@/lib/auth'
import { useRaccourcis } from '@/lib/raccourcis'

export default function MenuNavigation() {
  const { sectionCourante, naviguer } = useNav()
  const { user, logout } = useAuth()
  const { urlAgenda, ouvrirAgenda, ouvrirCanva, ouvrirDistriDoc, ouvrirEspaceDocPro } = useRaccourcis()
  const [ouvert, setOuvert] = useState(false)
  const conteneurRef = useRef<HTMLDivElement>(null)

  // Ferme au clic à l'extérieur et à la touche Échap (uniquement quand ouvert).
  useEffect(() => {
    if (!ouvert) return
    const surClic = (e: MouseEvent) => {
      if (conteneurRef.current && !conteneurRef.current.contains(e.target as Node)) {
        setOuvert(false)
      }
    }
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuvert(false)
    }
    document.addEventListener('mousedown', surClic)
    document.addEventListener('keydown', surTouche)
    return () => {
      document.removeEventListener('mousedown', surClic)
      document.removeEventListener('keydown', surTouche)
    }
  }, [ouvert])

  // Générateur de documents (BD-DistriDoc) : visible pendant l'ESSAI EN COURS **OU** avec un
  // ABONNEMENT ACTIF. Ailleurs dans l'application, le mur d'accès (src/App.tsx) rend la question
  // sans objet ; mais l'écran Réglages RESTE atteignable quand l'accès est échu, et il affiche ce
  // menu. Sans ce garde, une cliente à l'essai expiré y verrait encore l'entrée. On réutilise
  // `accesActif` (shared/calculs/abonnement.ts), source de vérité unique — aucune seconde règle.
  const lienExterneVisible = user ? accesActif(user) : false

  const allerVers = (section: SectionId) => {
    setOuvert(false)
    naviguer(section)
  }

  // Les entrées adminOnly ne sont visibles que pour l'admin ; les entrées moduleRequis
  // seulement si l'utilisatrice possède le module (ex. cosmetique).
  // Un module SUSPENDU (shared/constants.ts) retire ses entrées du menu pour TOUT LE MONDE.
  const sections = SECTIONS_MENU.filter(
    (s) =>
      (!s.adminOnly || user?.role === 'admin') &&
      (!s.moduleRequis || moduleOuvert(user?.modules, s.moduleRequis)),
  )

  return (
    <div ref={conteneurRef} className="relative">
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label="Menu de navigation"
        title="Menu"
        className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-lg leading-none text-bdspInk transition hover:bg-stone-50"
      >
        ☰
      </button>

      {ouvert && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-stone-200 bg-white py-1 shadow-xl"
        >
          {sections.map((s) => {
            const active = s.id === sectionCourante
            return (
              <button
                key={s.id}
                type="button"
                role="menuitem"
                onClick={() => allerVers(s.id)}
                aria-current={active ? 'page' : undefined}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition ${
                  active
                    ? 'bg-bdspIndigoSoft font-semibold text-bdspInk'
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                <span aria-hidden="true" className="text-base">
                  {s.emoji}
                </span>
                <span className="flex-1">{s.label}</span>
                {active && (
                  <span aria-hidden="true" className="text-bdspIndigo">
                    ✓
                  </span>
                )}
              </button>
            )
          })}

          <div className="my-1 border-t border-stone-200" />

          {urlAgenda && urlAgenda.trim() ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOuvert(false)
                ouvrirAgenda()
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
            >
              <span aria-hidden="true" className="text-base">
                📅
              </span>
              <span className="flex-1">Mon agenda ↗</span>
            </button>
          ) : null}

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOuvert(false)
              ouvrirCanva()
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
          >
            <span aria-hidden="true" className="text-base">
              🏷️
            </span>
            <span className="flex-1">Étiquette Canva ↗</span>
          </button>

          {lienExterneVisible ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOuvert(false)
                ouvrirDistriDoc()
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
            >
              <span aria-hidden="true" className="text-base">
                📄
              </span>
              <span className="flex-1">Générateur de documents ↗</span>
            </button>
          ) : null}

          {/* Espace client BD-DocPro : même garde d'accès que DistriDoc (abonnée à l'essai ou
              active), l'espace servant à suivre des dossiers réservés aux abonnées. Lien statique. */}
          {lienExterneVisible ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOuvert(false)
                ouvrirEspaceDocPro()
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
            >
              <span aria-hidden="true" className="text-base">
                🗂️
              </span>
              <span className="flex-1">Mon espace BD-DocPro ↗</span>
            </button>
          ) : null}

          <div className="my-1 border-t border-stone-200" />

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOuvert(false)
              void logout()
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-stone-50"
          >
            <span aria-hidden="true" className="text-base">
              🚪
            </span>
            <span className="flex-1">Se déconnecter</span>
          </button>
        </div>
      )}
    </div>
  )
}
